import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import Landing from "@/pages/Landing";
import LandingAr from "@/pages/LandingAr";

// ─────────────────────────────────────────────────────────────────
// Prototype E — Integrated Photographic Ribbon
//
// The navigation is ONE continuous photographic ribbon divided into
// clickable segments — not eight independent cards. The ribbon
// emerges from the black header at both ends via gradient fades and
// carries a shared cinematic grade at rest so all photographs feel
// like frames of the same visual world.
//
// Structural rules:
//   • No card borders, no rings, no rounded corners, no visible gaps,
//     no per-image backgrounds, no thumbnail-and-caption stack.
//   • Segments are pure flex-1 subdivisions of one ribbon.
//   • Missing imagery renders as a seamless deep-black segment with
//     only its label — never a debug marker in the interface.
//   • Header height 108px. Ribbon occupies the full header height.
//   • Labels sit on a shared bottom baseline inside the ribbon.
//   • Hover: only that segment regains grade; label brightens; a thin
//     gold lower-edge indicator appears. No border, no elevation, no
//     detach, no dropdown.
//
// Preservation:
//   • /, /ar, /prototypes/arrival, /prototypes/ambient,
//     /prototypes/visual-header, /prototypes/cinematic-header —
//     untouched. Landing.jsx and LandingAr.jsx not modified.
// ─────────────────────────────────────────────────────────────────

// Ribbon segment configuration. object-position tuned per image so
// the semantic subject survives cropping into a narrow segment.
const RIBBON = [
    {
        to: "/",
        end: true,
        labelEn: "HOME",
        labelAr: "الرئيسية",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/5n6pde8o_FB21FACC-7058-44F0-AC7A-FA1B1432698C.JPG",
        // Wide-body Supra hood commanding the crowd — arrival subject
        objectPos: "50% 42%",
    },
    {
        to: "/about",
        labelEn: "CrewZNatioN",
        labelAr: "CrewZNatioN",
        protectedEn: true,
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/u65gd357_IMG-20260503-WA0051.jpg",
        // Crowd interacting; keep faces high in frame
        objectPos: "50% 38%",
    },
    {
        to: "/gathering",
        labelEn: "The Gathering",
        labelAr: "The Gathering",
        protectedEn: true,
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/tf8y44s5_F3209F41-0B30-4C13-8807-7FCBDFB18ABD.PNG",
        // Preserve the "This is Riyadh" text/lights/crowd triad
        objectPos: "50% 40%",
    },
    {
        to: "/events",
        labelEn: "EVENTS",
        labelAr: "الفعاليات",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/l3h9h8v0_35B1CA2D-6A44-433F-A0BB-19EE971F256E.JPEG",
        // Three Corvette front fascias in a row — activity/lineup
        objectPos: "50% 48%",
    },
    {
        to: "/gallery",
        labelEn: "MOMENTS",
        labelAr: "اللحظات",
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/eyed6v8w_E4BE464A-17CA-400C-9126-D68CE352C4E2.JPG",
        // Purple lowrider with roses — subject is centered
        objectPos: "50% 55%",
    },
    {
        to: "/sponsors",
        labelEn: "PARTNERS",
        labelAr: "الشراكات",
        image: null, // Seamless black segment (see report)
    },
    {
        to: "/store",
        labelEn: "GEAR",
        labelAr: "GEAR",
        protectedEn: true,
        image: null,
    },
    {
        to: "/contact",
        labelEn: "WELCOME",
        labelAr: "حياك",
        image: null,
    },
];

function renderLabel(item, isAr) {
    if (item.labelEn === "The Gathering") {
        return (
            <span dir="ltr" className="inline-block">
                The <span className="text-[var(--crewz-gold)]">Gathering</span>
            </span>
        );
    }
    if (item.protectedEn && isAr) {
        return (
            <span dir="ltr" className="inline-block">
                {item.labelEn}
            </span>
        );
    }
    return isAr ? item.labelAr : item.labelEn;
}

function RibbonSegment({ item, isAr }) {
    const useArabicFont = isAr && !item.protectedEn && item.labelEn !== "The Gathering";
    return (
        <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
                `ribbon-segment group relative flex-1 h-full block overflow-hidden ${
                    isActive ? "is-active" : ""
                }`
            }
            data-testid={`ribbon-segment-${item.labelEn.toLowerCase().replace(/\s|&/g, "-")}`}
        >
            {({ isActive }) => (
                <>
                    {/* Photograph — deliberately cropped via
                        object-position so the semantic subject
                        survives the narrow segment width. */}
                    {item.image && (
                        <img
                            src={item.image}
                            alt=""
                            aria-hidden="true"
                            className="ribbon-image absolute inset-0 w-full h-full object-cover select-none"
                            style={{ objectPosition: item.objectPos }}
                            draggable={false}
                        />
                    )}

                    {/* Label — shared lower baseline; overlaid inside
                        the ribbon; no separate container. */}
                    <span
                        className={`ribbon-label absolute inset-x-0 bottom-3 px-3 flex items-baseline leading-none whitespace-nowrap ${
                            isAr ? "justify-end" : "justify-start"
                        } ${
                            useArabicFont
                                ? "font-arabic text-[12px] font-bold"
                                : "font-body uppercase tracking-[0.16em] text-[10px] font-bold"
                        } ${isActive ? "text-[var(--crewz-gold)]" : "text-white/85"}`}
                    >
                        {renderLabel(item, isAr)}
                    </span>

                    {/* Gold lower-edge indicator — hidden at rest,
                        grows in on hover / active. */}
                    <span
                        className="ribbon-underline absolute inset-x-0 bottom-0 h-[2px] bg-[var(--crewz-gold)] pointer-events-none"
                        aria-hidden="true"
                    />
                </>
            )}
        </NavLink>
    );
}

function IntegratedRibbonHeader({ isAr, toggleTo }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="ribbon-header sticky top-0 z-50 bg-[#0f0f0f] border-b border-white/5"
            data-testid="site-navbar-ribbon"
            dir={isAr ? "rtl" : "ltr"}
        >
            <div
                className="ribbon-frame relative flex items-stretch"
                style={{ height: "108px" }}
            >
                {/* Zone 1 — Utility (language toggle). Fixed width so
                    the ribbon can expand freely. In RTL, this zone
                    reverses natively via flex + dir. */}
                <div className="ribbon-utility hidden lg:flex items-center px-6 md:px-8" style={{ width: "128px" }}>
                    <Link
                        to={toggleTo}
                        className={
                            isAr
                                ? "font-body uppercase tracking-[0.2em] text-[12px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                                : "font-arabic normal-case tracking-normal text-[14px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                        }
                        dir={isAr ? "ltr" : "rtl"}
                        data-testid="ribbon-lang-switcher"
                    >
                        {isAr ? "English" : "العربية"}
                    </Link>
                </div>

                {/* Zone 2 — The photographic ribbon. One continuous
                    surface subdivided into flex-1 clickable segments.
                    No gaps, no borders, no rings. */}
                <div className="ribbon-track relative flex-1 h-full overflow-hidden">
                    {/* Segments layer */}
                    <div className="absolute inset-0 flex" data-testid="ribbon-track">
                        {RIBBON.map((item) => (
                            <RibbonSegment key={item.to} item={item} isAr={isAr} />
                        ))}
                    </div>

                    {/* Shared vertical gradient — one continuous top
                        and bottom darkening across the entire ribbon
                        so images merge into the header's blackness. */}
                    <div
                        className="ribbon-vfade absolute inset-0 pointer-events-none"
                        aria-hidden="true"
                        style={{
                            background:
                                "linear-gradient(to bottom, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.14) 20%, rgba(0,0,0,0.06) 45%, rgba(0,0,0,0.18) 75%, rgba(0,0,0,0.42) 100%)",
                        }}
                    />

                    {/* End fades — the ribbon dissolves into the
                        black header at both ends, so it doesn't read
                        as a pasted-on strip. */}
                    <div
                        className="ribbon-endfade-start absolute inset-y-0 left-0 pointer-events-none"
                        aria-hidden="true"
                        style={{
                            width: "56px",
                            background:
                                "linear-gradient(to right, #0f0f0f 0%, rgba(15,15,15,0.6) 55%, transparent 100%)",
                        }}
                    />
                    <div
                        className="ribbon-endfade-end absolute inset-y-0 right-0 pointer-events-none"
                        aria-hidden="true"
                        style={{
                            width: "56px",
                            background:
                                "linear-gradient(to left, #0f0f0f 0%, rgba(15,15,15,0.6) 55%, transparent 100%)",
                        }}
                    />
                </div>

                {/* Zone 3 — Wordmark. Fixed width matching Zone 1
                    footprint style for visual balance. */}
                <div className="ribbon-wordmark hidden lg:flex items-center px-6 md:px-8" style={{ width: "168px", justifyContent: isAr ? "flex-start" : "flex-end" }}>
                    <Link
                        to={isAr ? "/prototypes/integrated-ribbon?lang=ar" : "/prototypes/integrated-ribbon"}
                        className="flex items-center"
                        data-testid="ribbon-logo"
                    >
                        <img
                            src={LOGO_URL}
                            alt="CrewZNatioN"
                            className="h-14 md:h-16 w-auto select-none"
                            draggable={false}
                        />
                    </Link>
                </div>

                {/* Mobile — outside the prototype's declared scope.
                    Fall back to a text drawer, ribbon collapses on
                    small viewports. */}
                <div className="lg:hidden flex items-center justify-between w-full px-6">
                    <Link
                        to={isAr ? "/prototypes/integrated-ribbon?lang=ar" : "/prototypes/integrated-ribbon"}
                        className="flex items-center"
                        data-testid="ribbon-logo-mobile"
                    >
                        <img
                            src={LOGO_URL}
                            alt="CrewZNatioN"
                            className="h-12 w-auto select-none"
                            draggable={false}
                        />
                    </Link>
                    <button
                        className="text-white p-2"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="ribbon-mobile-toggle"
                        aria-label={isAr ? "فتح القائمة" : "Toggle menu"}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/5 bg-[#111111]/95 backdrop-blur-xl">
                    <div className={`px-6 py-6 flex flex-col gap-1 ${isAr ? "font-arabic" : "font-body"}`}>
                        {RIBBON.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `px-2 py-3 text-xs tracking-[0.18em] border-b border-white/5 ${
                                        isActive ? "text-[var(--crewz-gold)]" : "text-zinc-300"
                                    }`
                                }
                            >
                                {renderLabel(item, isAr)}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}

export default function IntegratedRibbonPrototype() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");
    const toggleTo = isAr
        ? "/prototypes/integrated-ribbon"
        : "/prototypes/integrated-ribbon?lang=ar";

    return (
        <div
            className="integrated-ribbon-prototype min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]"
            data-testid="integrated-ribbon-prototype"
            dir={isAr ? "rtl" : "ltr"}
        >
            <IntegratedRibbonHeader isAr={isAr} toggleTo={toggleTo} />

            {/* English → Landing. Arabic → LandingAr. */}
            <div className="integrated-ribbon-body">
                {isAr ? <LandingAr /> : <Landing />}
            </div>

            {/* Prototype-scoped CSS — cannot escape.
                Photograph grade at rest is applied via filter, so all
                images share one exposure/saturation/contrast baseline.
                Hover restores grade only for the hovered segment and
                animates the shared underline in. */}
            <style>{`
                .integrated-ribbon-prototype .integrated-ribbon-body [data-testid="site-navbar"],
                .integrated-ribbon-prototype .integrated-ribbon-body [data-testid="site-navbar-ar"] {
                    display: none !important;
                }

                /* Shared cinematic grade at rest — one visual world */
                .integrated-ribbon-prototype .ribbon-image {
                    filter: brightness(0.62) saturate(0.75) contrast(1.06);
                    transform: scale(1.001);
                    transform-origin: center;
                    transition:
                        filter 600ms cubic-bezier(0.22, 0.61, 0.36, 1),
                        transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1);
                    will-change: filter, transform;
                }

                /* Gold underline hidden at rest */
                .integrated-ribbon-prototype .ribbon-underline {
                    transform-origin: center;
                    transform: scaleX(0);
                    opacity: 0;
                    transition:
                        transform 400ms cubic-bezier(0.22, 0.61, 0.36, 1),
                        opacity 300ms ease-out;
                }

                /* Label baseline transition */
                .integrated-ribbon-prototype .ribbon-label {
                    transition: color 400ms ease-out, letter-spacing 400ms ease-out;
                }

                /* Hover — only the hovered segment lifts out of the
                   shared grade. No border, no elevation, no scale on
                   the segment itself. */
                .integrated-ribbon-prototype .ribbon-segment:hover .ribbon-image,
                .integrated-ribbon-prototype .ribbon-segment:focus-visible .ribbon-image {
                    filter: brightness(0.98) saturate(1.02) contrast(1.10);
                    transform: scale(1.02);
                }
                .integrated-ribbon-prototype .ribbon-segment:hover .ribbon-label,
                .integrated-ribbon-prototype .ribbon-segment:focus-visible .ribbon-label,
                .integrated-ribbon-prototype .ribbon-segment.is-active .ribbon-label {
                    color: #ffffff;
                }
                .integrated-ribbon-prototype .ribbon-segment:hover .ribbon-underline,
                .integrated-ribbon-prototype .ribbon-segment:focus-visible .ribbon-underline,
                .integrated-ribbon-prototype .ribbon-segment.is-active .ribbon-underline {
                    transform: scaleX(1);
                    opacity: 1;
                }

                /* Active state slightly emphasizes without leaving
                   the shared grade too far behind. */
                .integrated-ribbon-prototype .ribbon-segment.is-active .ribbon-image {
                    filter: brightness(0.78) saturate(0.9) contrast(1.08);
                }

                /* Focus outline: keyboard users see the gold underline
                   only; no default focus ring around the segment. */
                .integrated-ribbon-prototype .ribbon-segment:focus {
                    outline: none;
                }

                @media (prefers-reduced-motion: reduce) {
                    .integrated-ribbon-prototype .ribbon-image,
                    .integrated-ribbon-prototype .ribbon-underline,
                    .integrated-ribbon-prototype .ribbon-label {
                        transition: none;
                    }
                }
            `}</style>
        </div>
    );
}
